/**
 * Реестр механизмов.
 *
 * Единственное место, где перечислено, что вообще умеет модуль. Добавить
 * механизм = дописать сюда одну запись; ядро при этом не меняется ни строкой.
 *
 * `breath` стоит здесь с пометкой `fieldless`: он единственный не выражается
 * полем — размывается содержимое, а не маска. Ядро по этому флагу не пытается
 * звать `field` и не печёт лист, а проигрывание уходит в отдельную ветку.
 */

import { TEMPO } from '../core/pacing'
import { capillary, patches, threshold } from './organic'
import { bayer, halftone, hatch } from './print'
import { burn, crackle, torn } from './paper'
import type { Mechanism } from './types'

export const MECHANISMS: Mechanism[] = [
  {
    id: 'threshold',
    title: 'Порог по полю',
    note: 'Проявляется везде сразу, пятнами неправильной формы',
    field: threshold,
    tempo: TEMPO.ink!,
    feather: 0.055,
    lag: 0.06,
    minSide: 40,
    everyday: true,
  },
  {
    id: 'patches',
    title: 'Проявитель',
    note: 'Проступает в нескольких местах разом, как бумага в ванночке',
    field: patches,
    tempo: TEMPO.ink!,
    feather: 0.07,
    lag: 0.07,
    minSide: 56,
    everyday: true,
  },
  {
    id: 'capillary',
    title: 'Капилляр',
    note: 'Фронт идёт откуда-то и обтекает плотные места бумаги',
    field: capillary,
    tempo: TEMPO.linear!,
    feather: 0.05,
    lag: 0.055,
    minSide: 56,
    everyday: true,
  },
  {
    id: 'torn',
    parked: true,
    title: 'Рваный край',
    note: 'Кромка обрыва идёт поперёк листа',
    field: torn,
    tempo: TEMPO.linear!,
    feather: 0.03,
    lag: 0.03,
    minSide: 40,
    everyday: true,
  },
  {
    id: 'crackle',
    title: 'Кракелюр',
    note: 'Раскрывается от трещин наружу — залом, лак, сухая глина',
    field: crackle,
    tempo: TEMPO.linear!,
    feather: 0.04,
    lag: 0.05,
    minSide: 64,
    everyday: true,
  },
  {
    id: 'halftone',
    parked: true,
    title: 'Растровая точка',
    note: 'Точки набухают и смыкаются — газетный растр',
    field: halftone,
    tempo: TEMPO.linear!,
    feather: 0.03,
    lag: 0.04,
    minSide: 72,
    everyday: true,
  },
  {
    id: 'bayer',
    parked: true,
    title: 'Дизеринг',
    note: 'Сухое типографское растворение по решётке',
    field: bayer,
    tempo: TEMPO.linear!,
    feather: 0.02,
    lag: 0.03,
    minSide: 56,
    everyday: true,
  },
  {
    id: 'hatch',
    parked: true,
    title: 'Штриховка',
    note: 'Не проявляется, а рисуется: штрих ложится на штрих',
    field: hatch,
    tempo: TEMPO.linear!,
    feather: 0.035,
    lag: 0.04,
    minSide: 72,
    everyday: true,
  },
  {
    id: 'burn',
    parked: true,
    title: 'Прогорание',
    note: 'Лист занимается от очага, кромка тлеет. Для особых случаев',
    field: burn,
    tempo: TEMPO.catch!,
    feather: 0.045,
    // Ободок шире прочих: это обугленная кромка, её должно быть видно.
    // Ширина задаётся в единицах поля, а не в кадрах: отставание
    // на один кадр дало бы кромку в полтора десятка пикселей.
    lag: 0.085,
    minSide: 88,
    // В общий мешок не идёт — но не из-за цены. Цена у него обычная:
    // поле считается один раз, как у всех, и на нашем разрешении это
    // единицы миллисекунд. Дорогим прогорание называют замеры, сделанные
    // для полноразмерного поля; у нас оно печётся мельче.
    // Не в мешке оно потому, что слишком ГРОМКОЕ: такое событие уместно
    // на карте и на важном моменте, а не на каждом появлении блока.
    everyday: false,
  },
]

// Дыхание. Поля у него нет — см. `fieldless`. В общий мешок попадает
// только на малых площадях: стоимость размытия растёт с площадью,
// и на полном экране оно дороже всей остальной анимации вместе взятой.
MECHANISMS.push({
  id: 'breath',
  title: 'Дыхание',
  note: 'Проступает из глубины: расфокус уходит в резкость',
  field: () => new Float32Array(0),
  tempo: TEMPO.unfold!,
  feather: 0,
  lag: 0,
  minSide: 0,
  everyday: true,
  fieldless: true,
})

export const BY_ID: Record<string, Mechanism> = Object.fromEntries(
  MECHANISMS.map((m) => [m.id, m]),
)

/** Идентификаторы всех механизмов, включая неежедневные. */
export const MECHANISM_IDS = MECHANISMS.map((m) => m.id)

export type { FieldContext, FieldFn, Mechanism, MechanismMeta } from './types'
